import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

/**
 * A generic, top-level linkable iPad — the iPad-token/auth record + the whole device.
 *
 * Auth = a row found by `by_tokenHash` with `restaurantId !== null` (a linked device) → use its
 * `restaurantId`. The Keychain iPad token is the stable identity (survives reinstall). A `uuid`
 * (v4) is the single cross-link for everything that references this device from another document
 * (the Linkly `posId`, `posPrinter.attachedToIPadUuid`, `remoteCommands.targetPosTerminalUuid`) —
 * it is app-controlled so references survive a DB migration, unlike the deployment-local `_id`.
 * The new POS build reads this row directly, token-stripped via `iPadCvxWithoutTokenSchema`.
 *
 * Source-of-truth split: data the device reports about itself lives here; data the owner authors
 * lives in `posConfig`. The only owner-authored, posConfig-coupled fact (the USB printer↔device
 * attachment) is modelled on the printer (`posPrinter.attachedToIPadUuid`), not here.
 */
export const iPadSchema = z.object({
  /** sha256 (hex) of the raw Keychain iPad token — a high-entropy random bearer credential + stable identity; never exposed to clients. */
  tokenHash: z.string(),
  /** The linked restaurant, or `null` when unlinked (the device floats free until re-linked). */
  restaurantId: z.string().nullable().default(null),
  /**
   * UUID v4 identifying this iPad to the Linkly EFTPOS integration (its `posId`) and the single
   * value other documents use to reference this device. The Linkly server validates this as a v4
   * UUID on the auth-token request and rejects it if invalid, so it must be a real v4 (generated
   * via `iPadStrictSchema`), not an arbitrary string.
   * @see https://linkly.com.au/apidoc/REST/#auth-token-request
   */
  uuid: z.string().default(() => uuidv4()),
  /** Copied from the device code on link. Editable via `renameIPad`. */
  name: z.string().nullable().default(null),

  // Device-reported facts — written by the iPad via `pos.saveDeviceInfo`.
  /** e.g. `au.com.orderhub.pos.sta.v3` */
  appBundleId: z.string().nullable().default(null),
  /** e.g. "44" */
  appJsBuildVersion: z.string().nullable().default(null),
  /** e.g. "2.0.0" */
  appNativeBuildVersion: z.string().nullable().default(null),
  /**
   * iOS will change the vendor identifier (`idForVendor`) if all apps from the current app's vendor
   * (OrderHub) have been uninstalled.
   * @see https://docs.expo.dev/versions/latest/sdk/application/#applicationgetiosidforvendorasync
   */
  idForVendor: z.string().nullable().default(null),
  /** e.g. "15.7.1" */
  iosVersion: z.string().nullable().default(null),
  lanIp: z.string().nullable().default(null),
  /** e.g. "iPad (6th generation)" */
  modelName: z.string().nullable().default(null),
  /** e.g. "2018" */
  modelYear: z.string().nullable().default(null),

  linkedAt: z.number().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type IPad = z.output<typeof iPadSchema>;

export const iPadStrictSchema = iPadSchema.safeExtend({
  tokenHash: z.hash('sha256'),
  updatedAt: z.number().default(() => Date.now()),
});

export const iPadCvxSchema = iPadSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type IPadCvx = z.output<typeof iPadCvxSchema>;

/** An `IPadCvx` whose `restaurantId` is non-null — i.e. a *linked* device. */
export const linkedIPadCvxSchema = iPadCvxSchema.safeExtend({
  restaurantId: z.string(),
});
export type LinkedIPadCvx = z.output<typeof linkedIPadCvxSchema>;

/** An iPad shaped for clients — the stored row minus the secret `tokenHash`. */
export const iPadCvxWithoutTokenSchema = iPadCvxSchema.omit({ tokenHash: true });
export type IPadCvxWithoutToken = z.output<typeof iPadCvxWithoutTokenSchema>;
